// What the agent asks the portal to do to one of its own lists: show it, filtered to the rows the answer
// describes. Keep it import-free of React, so `node --test` can run listActions.test.ts against it directly.
//
// The instruction is CLIENT-BOUND but AGENT-SUPPLIED, so nothing in it is trusted: every column is checked
// against the grid actually on screen, every value is cleaned and capped, and the path it names is only
// followed when the app's own registry agrees that is where that list lives. What cannot be matched is
// dropped and named, so a step in the panel never claims more than the grid is showing.

import { clean, offerStore, pendingStore } from './proposalChannel.ts';

/** The key the agent's tool-call chunk carries its instruction under. */
export const APPLY_LIST_FILTER = 'applyListFilter';

// Moving the user to a list they do not have open is asked with the panel's own confirm affordance rather
// than with a sentence the agent has to remember to write, phrase and separate. enso renders it on a step
// whose call names a registered action, so the offer is a button on the step that would do the work, the
// filter travels in the call instead of being described in prose, and the answer beside it says nothing the
// user has to act on.

/** The action enso matches a confirmed step to. It is also the step's title, so it reads as one. */
export const OPEN_LIST_ACTION = 'Open the filtered list';

/**
 * JSON Schema enso checks the call against before offering it. One argument, and a readable one: the panel
 * prints a step's arguments under its title, so anything technical in here is technical on the user's screen.
 */
export const OPEN_LIST_PARAMETERS: Record<string, unknown> = {
  type: 'object',
  properties: { open: { type: 'string' } },
  required: ['open'],
};

/** What the offer would do, in the words the step shows and the user judges it by. */
export const describeOffer = (instruction: ListFilterInstruction): string => {
  const name = instruction.list ?? instruction.entity;
  const columns = Object.keys(instruction.filters ?? {});
  const search = instruction.search ?? '';
  if (columns.length > 0) return `${name}, filtered by ${columns.join(', ')}`;

  return search === '' ? name : `${name}, searched for ${search}`;
};

const offers = offerStore<ListFilterInstruction>(
  instruction => describeOffer(instruction),
  instruction => ({ ...instruction, openList: true }),
);

/** The call the panel offers: one readable argument, with the instruction kept against it. */
export const offerOpenListCall = offers.offer;

/** The instruction behind a confirmed call, or null when it is no longer remembered. */
export const takeOpenListOffer = offers.take;

export const forgetOpenListOffers = offers.forget;

/** The part of an enso message this needs, to tell a fresh question from the round after a click. */
export interface TurnMessage {
  role: string;
  /** Set only on a `tool` message: the id of the call it answers. */
  toolCallId?: string;
  toolCalls?: { id: string; name: string }[];
}

/**
 * Whether this round is only the panel coming back after the user settled one of the offers it painted.
 *
 * enso runs a turn as rounds: it calls the transport, dispatches whatever the round proposed, appends the
 * result as a `tool` message, and calls the transport again so the model can react. Nothing here needs the
 * model to react. The step already says what happened and the grid on screen shows it, while asking the agent
 * again re-runs the whole turn against a user message it has already answered, pastes a second answer under
 * the first, and grows the step chain above text the user has already read.
 */
export const isSettledOfferRound = (messages: readonly TurnMessage[], actions: readonly string[] = [OPEN_LIST_ACTION]): boolean => {
  const last = messages[messages.length - 1];
  if (last === undefined || last.role !== 'tool' || last.toolCallId === undefined) return false;

  return messages.some(message => (message.toolCalls ?? []).some(call => call.id === last.toolCallId && actions.includes(call.name)));
};



/** Longest value applied to a column, and the most columns and values honoured, mirroring the page context caps. */
export const MAX_FILTER_COLUMNS = 15;
export const MAX_FILTER_VALUES = 25;
export const MAX_FILTER_LENGTH = 100;
export const MAX_SEARCH_LENGTH = 200;

/** The instruction as it arrives on the wire. Every field is checked before it is used. */
export interface ListFilterInstruction {
  entity: string;
  list?: string;
  path?: string;
  filters?: Record<string, unknown>;
  search?: string;
  /** The user has agreed to be taken to this list. Without it a list that is not on screen is left alone. */
  openList?: boolean;
}

/** What the grid was actually told to do, once the instruction was checked against it. */
export interface AppliedListFilter {
  entity: string;
  /** Column keys and values the grid accepted. */
  filters: Record<string, string[]>;
  search: string;
  /** Columns the instruction named that this grid does not have. */
  dropped: string[];
  /** Columns the instruction named that this grid has but offers no filter on. */
  unfilterable: string[];
}

/**
 * How a mounted list is reached. enso rebuilds its imperative handle on every render, so holding on to the
 * object read at mount pins the grid as it was then: it answers `getState()` from that render's columns
 * forever, whatever the grid has since become. Everything here goes through a reader instead.
 */
export type ReadListController = () => ListController | null;

/** The part of enso's table ref this needs. Restated structurally so this module imports nothing. */
export interface ListController {
  /**
   * The keys of the columns that offer a filter, which enso's state does not report. Without it every column
   * the grid has is taken as one.
   */
  filterable?: readonly string[];
  getState: () => {
    columns: { visible: { key?: unknown }[]; hidden: { key?: unknown }[] };
    filters: { byColumn: Record<string, unknown> };
    search: { text?: string };
  };
  setFilters: (filters: Record<string, unknown>, filterParams?: undefined, options?: { persist?: boolean }) => void;
  setSearch: (text: string, options?: { commit?: boolean }) => void;
  resetFilteredData: () => void;
}

/** The instruction carried by a tool-call chunk, or null when the chunk carries none this can act on. */
export const readListFilterInstruction = (args: unknown): ListFilterInstruction | null => {
  if (args === null || typeof args !== 'object') return null;

  const payload = (args as Record<string, unknown>)[APPLY_LIST_FILTER];
  if (payload === null || typeof payload !== 'object') return null;

  const { entity, list, path, filters, search, openList } = payload as Record<string, unknown>;
  if (typeof entity !== 'string' || clean(entity, MAX_FILTER_LENGTH) === '') return null;

  return {
    entity: clean(entity, MAX_FILTER_LENGTH),
    list: typeof list === 'string' ? clean(list, MAX_FILTER_LENGTH) : undefined,
    path: typeof path === 'string' ? path : undefined,
    filters: filters !== null && typeof filters === 'object' ? (filters as Record<string, unknown>) : undefined,
    search: typeof search === 'string' ? clean(search, MAX_SEARCH_LENGTH) : undefined,
    openList: openList === true,
  };
};

const columnKeys = (controller: ListController): string[] => {
  const { columns } = controller.getState();
  return [...columns.visible, ...columns.hidden].map(column => String(column.key)).filter(key => key !== 'undefined');
};

const values = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value])
    .filter(entry => typeof entry === 'string' || typeof entry === 'number')
    .map(entry => clean(String(entry), MAX_FILTER_LENGTH))
    .filter(entry => entry !== '')
    .slice(0, MAX_FILTER_VALUES);

/** The columns an instruction named that a grid could not filter by, split by why. */
export interface UnhonouredFilters {
  dropped: string[];
  unfilterable: string[];
}

/**
 * The filters the grid on screen can honour, keyed by its own column keys, and the ones it cannot. The
 * columns come from the grid rather than from a registry the agent holds a copy of: a column added at
 * runtime is filterable here without anything being declared twice, as long as it offers a filter. One that
 * does not would be set without narrowing a single row, while the step said the list was filtered by it.
 */
export const resolveFilters = (
  instruction: ListFilterInstruction,
  controller: ListController,
): { filters: Record<string, string[]> } & UnhonouredFilters => {
  const keys = columnKeys(controller);
  const filters: Record<string, string[]> = {};
  const dropped: string[] = [];
  const unfilterable: string[] = [];

  Object.entries(instruction.filters ?? {}).forEach(([column, value]) => {
    if (Object.keys(filters).length >= MAX_FILTER_COLUMNS) return;

    const key = clean(column, MAX_FILTER_LENGTH);
    const wanted = values(value);
    if (key === '' || wanted.length === 0 || !keys.includes(key)) dropped.push(column);
    else if (controller.filterable !== undefined && !controller.filterable.includes(key)) unfilterable.push(column);
    else filters[key] = wanted;
  });

  return { filters, dropped, unfilterable };
};

/**
 * Applies the instruction to one grid, replacing whatever it was filtered by: the answer beside it describes
 * this set of rows and no other. Returns what was applied, or null when nothing the grid can filter by was named.
 *
 * `persist: false`, as for a filter arriving from the URL: what the agent applied while answering a question
 * must not become the view the user is given the next time they open that list. `commit: false` on the search
 * for the same reason it is used there, so applying does not report a search the user never typed.
 */
export const applyListFilter = (instruction: ListFilterInstruction, controller: ListController): AppliedListFilter | null => {
  const { filters, dropped, unfilterable } = resolveFilters(instruction, controller);
  const search = instruction.search ?? '';
  if (Object.keys(filters).length === 0 && search === '' && dropped.length + unfilterable.length > 0) return null;

  const current = controller.getState().filters.byColumn;
  const patch: Record<string, unknown> = { ...filters };
  Object.keys(current).forEach(column => {
    if (!(column in patch)) patch[column] = null;
  });

  if (Object.keys(patch).length > 0) {
    controller.setFilters(patch, undefined, { persist: false });
    // The export reads AntD's filtered snapshot, which only refreshes on an AntD change. Dropping it keeps
    // the export off the rows the last column-filter interaction left behind.
    controller.resetFilteredData();
  }

  if ((controller.getState().search.text ?? '') !== search) controller.setSearch(search, { commit: false });

  return { entity: instruction.entity, filters, search, dropped, unfilterable };
};

const unhonoured = ({ dropped, unfilterable }: UnhonouredFilters): string =>
  [
    ...(dropped.length === 0 ? [] : [`has no ${dropped.join(', ')}`]),
    ...(unfilterable.length === 0 ? [] : [`cannot be filtered by ${unfilterable.join(', ')}`]),
  ].join(' and ');

/** The step's result line: what the user can see happened, including what the grid could not honour. */
export const describeApplied = (applied: AppliedListFilter, listName: string | undefined): string => {
  const name = listName ?? applied.entity;
  const columns = Object.keys(applied.filters).length;
  const filtered = columns === 0 ? `${name} shown` : `${name} filtered by ${Object.keys(applied.filters).join(', ')}`;
  const notHonoured = unhonoured(applied);

  return notHonoured === '' ? filtered : `${filtered}; this list ${notHonoured}`;
};

/** The step's result line when the grid could honour none of the columns named. */
export const describeNothingFiltered = (listName: string, columns: UnhonouredFilters): string =>
  `${listName} ${unhonoured(columns)}, so nothing was filtered`;

// An instruction for a list that is not on screen waits here until that list arrives. It survives the one
// navigation it takes to get there and nothing more: it expires, it is dropped once drained, and a newer one
// replaces it. Kept in this module rather than beside the controller registry so it stays import-free and
// `node --test` can drive it directly.

const pending = pendingStore<ListFilterInstruction>();

/** Holds an instruction for a list that is not on screen, replacing any older one. */
export const holdListFilter = pending.hold;

/** The instruction waiting for this list, if it is still current, with its deadline. Taking it clears it. */
export const takeListFilter = pending.take;

export const clearPendingListFilter = (): void => {
  pending.clear();
  offered = null;
  forgetOpenListOffers();
};

// What the agent offered to open, kept so that the user's yes buys what they were offered. The agent is
// supposed to send the same filters again on the confirming turn and does not always manage it, and a list
// opened bare answers a question nobody asked. No deadline: an offer is replaced by the next one and consumed
// by the agreement it was made for, so it cannot outlive either.
let offered: ListFilterInstruction | null = null;

export const holdListOffer = (instruction: ListFilterInstruction): void => {
  offered = instruction;
};

/**
 * The instruction to act on once the user has agreed. An agreement that names nothing to filter by takes what
 * was offered, since that is what was agreed to. One that names its own filters is left exactly as it is: the
 * agent asking for something different is a different answer, not a lossy copy of this one, and quietly
 * merging the two would filter the list by rows no answer describes.
 */
export const withOfferedFilters = (instruction: ListFilterInstruction): ListFilterInstruction => {
  const offer = offered;
  offered = null;
  if (offer === null || offer.entity !== instruction.entity) return instruction;

  const namesNothing = Object.keys(instruction.filters ?? {}).length === 0 && (instruction.search ?? '') === '';
  return namesNothing
    ? { ...instruction, filters: offer.filters, search: offer.search, path: instruction.path ?? offer.path }
    : instruction;
};

/**
 * Applies the instruction waiting for this list, and says whether the caller is done.
 *
 * False means one is still waiting: a grid reports no columns until enso's own restore has committed, which
 * is a render after the list mounts, so a filter drained on mount alone matches nothing and would be dropped
 * on a list that is about to be able to honour it. The caller retries until this returns true, which it does
 * as soon as the filter lands, when there is nothing waiting, and when the deadline passes.
 *
 * The grid is read afresh on every attempt, never held from the last one, or every attempt would ask the
 * same stale handle the same question and get the same answer.
 */
export const drainListFilter = (entityType: string, read: ReadListController): boolean => {
  const waiting = takeListFilter(entityType);
  if (waiting === null) return true;

  const controller = read();
  if (controller === null) {
    holdListFilter(waiting.instruction, waiting.expiresAt);
    return false;
  }

  if (applyListFilter(waiting.instruction, controller) !== null) return true;

  holdListFilter(waiting.instruction, waiting.expiresAt);
  return false;
};
